/**
 * Cliente HTTP da API do Movhuman.
 *
 * O contrato do erro está em docs/03_API_PYTHON.md §7: a API responde
 * `{ error: { code, message, details, request_id } }` e **`code` é a fonte de
 * verdade**. O `message` que vem do servidor existe para log e para clientes
 * que não traduzam; quem traduz é o frontend, por `errors.json`.
 *
 * Consequência prática: trocar o texto de um erro é editar um JSON de tradução,
 * sem tocar no backend nem redeployar a API.
 */

import i18next from 'i18next';

import { currentAccessToken } from './supabase';

const BASE_URL = (import.meta.env.VITE_API_BASE_URL ?? '/api').replace(/\/$/, '');

export interface ApiErrorBody {
  code: string;
  message: string;
  details: Record<string, unknown>;
  request_id: string;
}

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details: Record<string, unknown>;
  readonly requestId: string;

  constructor(status: number, body: ApiErrorBody) {
    // A mensagem legível vem do catálogo local, com o texto da API como
    // último recurso — se um código novo chegar antes de a tradução existir,
    // a pessoa vê a frase do servidor em vez de uma chave crua.
    super(i18next.t(`errors:${body.code}`, { defaultValue: body.message }));
    this.name = 'ApiError';
    this.code = body.code;
    this.status = status;
    this.details = body.details ?? {};
    this.requestId = body.request_id ?? '';
  }
}

export class NetworkError extends Error {
  readonly code = 'network_unreachable';

  constructor() {
    super(i18next.t('errors:network_unreachable'));
    this.name = 'NetworkError';
  }
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  /** Torna a repetição segura em POST/PATCH. Ver docs/03_API_PYTHON.md §4. */
  idempotencyKey?: string;
  signal?: AbortSignal;
}

export async function apiFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, idempotencyKey, headers, ...rest } = options;
  const token = await currentAccessToken();

  const requestHeaders = new Headers(headers);
  requestHeaders.set('Accept', 'application/json');
  // A API é agnóstica de idioma, mas usa isto para a mensagem-reserva e para
  // decidir o idioma de e-mails que a requisição venha a disparar.
  requestHeaders.set('Accept-Language', i18next.language);
  if (token) requestHeaders.set('Authorization', `Bearer ${token}`);
  // `FormData` é envio de arquivo, e o `Content-Type` dele **não** se escreve à
  // mão: o navegador precisa acrescentar o `boundary`, que só ele conhece.
  // Defini-lo aqui produziria um corpo que o servidor não consegue separar.
  const isForm = body instanceof FormData;
  if (body !== undefined && !isForm) requestHeaders.set('Content-Type', 'application/json');
  if (idempotencyKey) requestHeaders.set('Idempotency-Key', idempotencyKey);

  let response: Response;
  try {
    response = await fetch(`${BASE_URL}${path}`, {
      ...rest,
      headers: requestHeaders,
      body: body === undefined ? undefined : isForm ? (body as FormData) : JSON.stringify(body),
    });
  } catch {
    // `fetch` só rejeita por falha de rede — status 4xx e 5xx resolvem
    // normalmente. Chegar aqui significa que não houve resposta nenhuma.
    throw new NetworkError();
  }

  if (response.status === 204) return undefined as T;

  const payload = await response.json().catch(() => null);

  if (!response.ok) {
    const error = (payload as { error?: ApiErrorBody } | null)?.error;
    throw new ApiError(
      response.status,
      error ?? {
        code: 'unknown',
        message: '',
        details: {},
        request_id: response.headers.get('x-request-id') ?? '',
      },
    );
  }

  return payload as T;
}

export const api = {
  get: <T>(path: string, options?: RequestOptions) =>
    apiFetch<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    apiFetch<T>(path, { ...options, method: 'POST', body }),
  // `PUT` para os formulários que enviam o estado inteiro. Com `PATCH`,
  // desmarcar todas as opções de um campo seria indistinguível de não ter
  // tocado nele — e a tela de perfil é quase toda feita de listas de seleção.
  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    apiFetch<T>(path, { ...options, method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    apiFetch<T>(path, { ...options, method: 'PATCH', body }),
  delete: <T>(path: string, options?: RequestOptions) =>
    apiFetch<T>(path, { ...options, method: 'DELETE' }),
};
